// react
import React, { useEffect, useState } from "react";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { useMediaQuery, Box, SwipeableDrawer, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Button, IconButton, Typography, TextField } from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";

// ui-components
import { Puller } from "ui-components/Puller";
import { LoadingDialog } from "ui-components/LoadingComponent";

// user-controller
import activateAPIKey from "user-controller/activateAPIKey";

// A ---------------------------------------------------------------------- M

const getButtonStyle = (action) => ({
  py: 0.5,
  borderColor: "divider",
  color: action === "close" ? "error.main" : "primary.main",
  textTransform: "none",
  boxShadow: "none",
  borderRadius: 10,
  "&:hover": {
    borderColor: action === "close" ? "error.dark" : "primary.dark",
    backgroundColor: "action.hover",
  },
});

const ActivateAPIKey = ({ APIKey, open, setOpen, handleClose, setOpenAPIKeySnackbarSuccessful, setOpenAPIKeySnackbarError }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handlePopstate = () => {
      if (open) handleReset();
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open]);

  const handleActivate = async () => {
    setLoading(true);
    try {
      const data = { APIKey, name, company, type };
      const activation = await activateAPIKey(user.uid, data);
      if (activation.success) {
        console.log("API key created successfully");
        setOpenAPIKeySnackbarSuccessful(true);
      } else {
        console.log("Error in activating API key");
        setOpenAPIKeySnackbarError(true);
      }
    } catch (error) {
      console.error("Error in handleActivate:", error);
      setOpenAPIKeySnackbarError(true);
    } finally {
      setLoading(false);
      handleReset();
    }
  };

  const handleReset = () => {
    handleClose();
    setName("");
    setCompany("");
    setType("");
  };

  const DialogTitleContent = () => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid item>
        <Typography variant="h6" fontWeight="bold">
          {t("activate_api_key")}
        </Typography>
      </Grid>
      <Grid item>
        <IconButton onClick={handleReset} color="error" edge="end">
          <CloseOutlinedIcon />
        </IconButton>
      </Grid>
    </Grid>
  );

  const innerContent = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField margin="dense" id="value" label={t("value")} name="value" required fullWidth variant="outlined" value={APIKey} />
      </Grid>
      <Grid item xs={12}>
        <TextField margin="dense" id="Name" label={t("name")} name="Name" required fullWidth variant="outlined" value={name} onChange={(e) => setName(e.target.value)} />
      </Grid>
      <Grid item xs={12}>
        <TextField margin="dense" id="Company" label={t("company")} name="Name" fullWidth variant="outlined" value={company} onChange={(e) => setCompany(e.target.value)} />
      </Grid>
      <Grid item xs={12}>
        <TextField margin="dense" id="type" label={t("type")} name="Type" fullWidth variant="outlined" value={type} onChange={(e) => setType(e.target.value)} />
      </Grid>
    </Grid>
  );

  if (loading) return <LoadingDialog open={loading} />;

  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleReset}
      onOpen={() => setOpen(true)}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          height: "65%",
          borderTopLeftRadius: "4%",
          borderTopRightRadius: "4%",
        },
      }}
    >
      <Puller />
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" textAlign="center" mt={2} mb={3}>
          {t("activate_api_key")}
        </Typography>
        {innerContent}
        <Grid container spacing={1} mt={5}>
          <Grid item xs={12}>
            <Button fullWidth variant="outlined" sx={getButtonStyle("close")} onClick={handleReset}>
              {t("cancel")}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button fullWidth variant="outlined" sx={getButtonStyle("open")} onClick={handleActivate} disabled={!APIKey || !name}>
              {t("activate")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </SwipeableDrawer>
  ) : (
    <Dialog
      open={open}
      onClose={handleReset}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 2,
          p: 2,
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle>
        <DialogTitleContent />
      </DialogTitle>
      <DialogContent>{innerContent}</DialogContent>
      <DialogActions>
        <Button variant="outlined" sx={getButtonStyle("close")} onClick={handleReset}>
          {t("cancel")}
        </Button>
        <Button variant="outlined" sx={getButtonStyle("open")} onClick={handleActivate} disabled={!APIKey || !name}>
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivateAPIKey;
